import { EnvironmentId } from "@afar-research/contracts";
import * as Context from "effect/Context";
import * as Effect from "effect/Effect";
import * as Equal from "effect/Equal";
import * as Exit from "effect/Exit";
import * as Layer from "effect/Layer";
import * as Option from "effect/Option";
import * as Schema from "effect/Schema";
import * as Scope from "effect/Scope";
import * as Semaphore from "effect/Semaphore";
import * as Stream from "effect/Stream";
import * as SubscriptionRef from "effect/SubscriptionRef";

import * as Connectivity from "./connectivity.ts";
import * as ConnectionDriver from "./driver.ts";
import {
  hostEnvironmentEntry,
  type HostEnvironmentEntry,
  type HostEnvironmentRegistration,
} from "./host.ts";
import type { NetworkStatus, PreparedConnection, SupervisorConnectionState } from "./model.ts";
import * as EnvironmentSupervisor from "./supervisor.ts";
import * as ConnectionWakeups from "./wakeups.ts";
import { ClientTransportResources, trackClientTransportEffect } from "./resources.ts";

export class EnvironmentNotRegisteredError extends Schema.TaggedErrorClass<EnvironmentNotRegisteredError>()(
  "EnvironmentNotRegisteredError",
  { environmentId: EnvironmentId },
) {
  override get message(): string {
    return `Environment ${this.environmentId} is not registered.`;
  }
}

export class EnvironmentRegistry extends Context.Service<
  EnvironmentRegistry,
  {
    readonly entries: SubscriptionRef.SubscriptionRef<
      ReadonlyMap<EnvironmentId, HostEnvironmentEntry>
    >;
    readonly networkStatus: SubscriptionRef.SubscriptionRef<NetworkStatus>;
    readonly installHost: (registration: HostEnvironmentRegistration) => Effect.Effect<void>;
    readonly replaceHost: (registration: HostEnvironmentRegistration) => Effect.Effect<void>;
    readonly removeHost: (environmentId: EnvironmentId) => Effect.Effect<void>;
    /**
     * Switches a registered Environment on or off. Off drops the socket and stops the retry
     * ladder; the registration, its credentials, and its cached data are untouched, so switching
     * back on is the whole way back.
     */
    readonly setEnabled: (environmentId: EnvironmentId, enabled: boolean) => Effect.Effect<void>;
    readonly retryNow: (environmentId: EnvironmentId) => Effect.Effect<void>;
    readonly state: (
      environmentId: EnvironmentId,
    ) => Effect.Effect<SupervisorConnectionState, EnvironmentNotRegisteredError>;
    readonly stateChanges: (
      environmentId: EnvironmentId,
    ) => Stream.Stream<SupervisorConnectionState, EnvironmentNotRegisteredError>;
    readonly run: <A, E, R>(
      environmentId: EnvironmentId,
      effect: Effect.Effect<A, E, R>,
    ) => Effect.Effect<
      A,
      E | EnvironmentNotRegisteredError,
      Exclude<R, EnvironmentSupervisor.EnvironmentSupervisor>
    >;
    readonly runStream: <A, E, R>(
      environmentId: EnvironmentId,
      stream: Stream.Stream<A, E, R>,
    ) => Stream.Stream<
      A,
      E | EnvironmentNotRegisteredError,
      Exclude<R, EnvironmentSupervisor.EnvironmentSupervisor>
    >;
    readonly followStream: <A, E, R>(
      environmentId: EnvironmentId,
      stream: Stream.Stream<A, E, R>,
    ) => Stream.Stream<A, E, Exclude<R, EnvironmentSupervisor.EnvironmentSupervisor>>;
    /**
     * The Environment's prepared connection right now, read synchronously from its supervisor.
     * It needs no subscriber, so code outside a rendered view can call it; it is empty until
     * the Environment has been reached.
     */
    readonly preparedConnection: (
      environmentId: EnvironmentId,
    ) => Option.Option<PreparedConnection>;
    /**
     * The Environment's prepared connection, waiting until it has one. Interrupt it to stop
     * waiting: an Environment that is never reached, or never registered, never answers.
     */
    readonly awaitPreparedConnection: (
      environmentId: EnvironmentId,
    ) => Effect.Effect<PreparedConnection>;
  }
>()("@afar-research/client-runtime/connection/registry/EnvironmentRegistry") {}

interface EnvironmentServiceScope {
  readonly entry: HostEnvironmentEntry;
  readonly supervisor: EnvironmentSupervisor.EnvironmentSupervisor["Service"];
  readonly scope: Scope.Closeable;
}

export const make = Effect.gen(function* () {
  const connectivity = yield* Connectivity.Connectivity;
  const driver = yield* ConnectionDriver.ConnectionDriver;
  const wakeups = yield* ConnectionWakeups.ConnectionWakeups;
  const resources = yield* ClientTransportResources;
  const entries = yield* SubscriptionRef.make<ReadonlyMap<EnvironmentId, HostEnvironmentEntry>>(
    new Map(),
  );
  const networkStatus = yield* SubscriptionRef.make(yield* connectivity.status);
  const serviceScopes = yield* SubscriptionRef.make<
    ReadonlyMap<EnvironmentId, EnvironmentServiceScope>
  >(new Map());
  const scopeGuard = yield* Semaphore.make(1);

  const getEntry = Effect.fn("EnvironmentRegistry.getEntry")(function* (
    environmentId: EnvironmentId,
  ) {
    const entry = (yield* SubscriptionRef.get(entries)).get(environmentId);
    if (entry === undefined) {
      return yield* new EnvironmentNotRegisteredError({ environmentId });
    }
    return entry;
  });

  const closeAllScopes = Effect.fn("EnvironmentRegistry.closeAllScopes")(function* () {
    const current = yield* SubscriptionRef.get(serviceScopes);
    yield* SubscriptionRef.set(serviceScopes, new Map());
    yield* Effect.forEach(current.values(), (lease) => Scope.close(lease.scope, Exit.void), {
      concurrency: "unbounded",
      discard: true,
    });
  });

  const closeServiceScope = Effect.fn("EnvironmentRegistry.closeServiceScope")(function* (
    environmentId: EnvironmentId,
  ) {
    const current = yield* SubscriptionRef.get(serviceScopes);
    const lease = current.get(environmentId);
    if (lease === undefined) return;
    const next = new Map(current);
    next.delete(environmentId);
    yield* SubscriptionRef.set(serviceScopes, next);
    yield* Scope.close(lease.scope, Exit.void);
  });

  const createServiceScope = Effect.fn("EnvironmentRegistry.createServiceScope")(
    (entry: HostEnvironmentEntry) =>
      Effect.uninterruptible(
        Effect.gen(function* () {
          const scope = yield* Scope.make();
          const supervisor = yield* EnvironmentSupervisor.make(entry, {
            initiallyDesired: false,
          }).pipe(
            Effect.provideService(Connectivity.Connectivity, connectivity),
            Effect.provideService(ConnectionDriver.ConnectionDriver, driver),
            Effect.provideService(ConnectionWakeups.ConnectionWakeups, wakeups),
            Scope.provide(scope),
            Effect.onError(() => Scope.close(scope, Exit.void)),
          );
          // A switched-off Environment gets a supervisor but never a connection: it must hold no
          // socket and spend no retries until the user switches it back on.
          if (entry.enabled) yield* supervisor.connect;
          const current = yield* SubscriptionRef.get(serviceScopes);
          const next = new Map(current);
          next.set(entry.target.environmentId, { entry, supervisor, scope });
          yield* SubscriptionRef.set(serviceScopes, next);
          return supervisor;
        }),
      ),
  );

  const acquireSupervisor = Effect.fn("EnvironmentRegistry.acquireSupervisor")(function* (
    environmentId: EnvironmentId,
  ) {
    return yield* scopeGuard.withPermits(1)(
      Effect.gen(function* () {
        const entry = yield* getEntry(environmentId);
        const existing = (yield* SubscriptionRef.get(serviceScopes)).get(environmentId);
        if (existing !== undefined && Equal.equals(existing.entry, entry)) {
          return existing.supervisor;
        }
        yield* closeServiceScope(environmentId);
        return yield* createServiceScope(entry);
      }),
    );
  });

  const installHostWithMode = Effect.fn("EnvironmentRegistry.installHostWithMode")(function* (
    registration: HostEnvironmentRegistration,
    replace: boolean,
  ) {
    yield* scopeGuard.withPermits(1)(
      Effect.gen(function* () {
        const currentEntries = yield* SubscriptionRef.get(entries);
        const current = currentEntries.get(registration.target.environmentId);
        // Re-registration happens on every discovery pass. Carrying the previous switch forward
        // keeps a machine the user switched off from coming back on by itself.
        const entry = hostEnvironmentEntry(
          registration,
          current === undefined ? {} : { enabled: current.enabled },
        );
        // The Environment this client is hosted by is reached directly; an entry that reaches the
        // same Environment through a tunnel or an SSH forward describes a longer way round to the
        // same place and must not replace it.
        if (
          current?.target._tag === "PrimaryConnectionTarget" &&
          entry.target._tag !== "PrimaryConnectionTarget"
        )
          return;
        if (!replace && current !== undefined && Equal.equals(current, entry)) {
          return;
        }
        yield* closeServiceScope(entry.target.environmentId);
        const nextEntries = new Map(currentEntries);
        nextEntries.set(entry.target.environmentId, entry);
        yield* SubscriptionRef.set(entries, nextEntries);
        yield* createServiceScope(entry);
      }),
    );
  });

  const installHost = (registration: HostEnvironmentRegistration) =>
    installHostWithMode(registration, false);
  const replaceHost = (registration: HostEnvironmentRegistration) =>
    installHostWithMode(registration, true);

  const removeHost = Effect.fn("EnvironmentRegistry.removeHost")(function* (
    environmentId: EnvironmentId,
  ) {
    yield* scopeGuard.withPermits(1)(
      Effect.gen(function* () {
        const currentEntries = yield* SubscriptionRef.get(entries);
        if (!currentEntries.has(environmentId)) return;
        yield* closeServiceScope(environmentId);
        const nextEntries = new Map(currentEntries);
        nextEntries.delete(environmentId);
        yield* SubscriptionRef.set(entries, nextEntries);
      }),
    );
  });

  const setEnabled = Effect.fn("EnvironmentRegistry.setEnabled")(function* (
    environmentId: EnvironmentId,
    enabled: boolean,
  ) {
    yield* scopeGuard.withPermits(1)(
      Effect.gen(function* () {
        const currentEntries = yield* SubscriptionRef.get(entries);
        const current = currentEntries.get(environmentId);
        if (current === undefined || current.enabled === enabled) return;
        const entry: HostEnvironmentEntry = { ...current, enabled };
        // The scope is torn down either way: switching off has to drop the live socket, and
        // switching on needs a supervisor built from an entry that now says it may connect.
        yield* closeServiceScope(environmentId);
        const nextEntries = new Map(currentEntries);
        nextEntries.set(environmentId, entry);
        yield* SubscriptionRef.set(entries, nextEntries);
        yield* createServiceScope(entry);
      }),
    );
  });

  const run: EnvironmentRegistry["Service"]["run"] = Effect.fn("EnvironmentRegistry.run")(
    function* <A, E, R>(environmentId: EnvironmentId, effect: Effect.Effect<A, E, R>) {
      const supervisor = yield* acquireSupervisor(environmentId);
      return yield* Effect.provideService(
        effect,
        EnvironmentSupervisor.EnvironmentSupervisor,
        supervisor,
      );
    },
  );

  const runStream: EnvironmentRegistry["Service"]["runStream"] = <A, E, R>(
    environmentId: EnvironmentId,
    stream: Stream.Stream<A, E, R>,
  ) =>
    Stream.unwrap(
      acquireSupervisor(environmentId).pipe(
        Effect.map((supervisor) =>
          Stream.provideService(stream, EnvironmentSupervisor.EnvironmentSupervisor, supervisor),
        ),
      ),
    );

  const followStream: EnvironmentRegistry["Service"]["followStream"] = <A, E, R>(
    environmentId: EnvironmentId,
    stream: Stream.Stream<A, E, R>,
  ) =>
    SubscriptionRef.changes(entries).pipe(
      Stream.map((current) => Option.fromUndefinedOr(current.get(environmentId))),
      Stream.changes,
      Stream.switchMap(
        Option.match({
          onNone: () => Stream.empty,
          onSome: () =>
            Stream.unwrap(
              acquireSupervisor(environmentId).pipe(
                Effect.match({
                  onFailure: () => Stream.empty,
                  onSuccess: (supervisor) =>
                    Stream.provideService(
                      stream,
                      EnvironmentSupervisor.EnvironmentSupervisor,
                      supervisor,
                    ),
                }),
              ),
            ),
        }),
      ),
    );

  const retryNow = (environmentId: EnvironmentId) =>
    acquireSupervisor(environmentId).pipe(
      Effect.flatMap((supervisor) => supervisor.retryNow),
      Effect.catchTag("EnvironmentNotRegisteredError", () => Effect.void),
      Effect.withSpan("EnvironmentRegistry.retryNow"),
    );
  const state = Effect.fn("EnvironmentRegistry.state")(function* (environmentId: EnvironmentId) {
    const supervisor = yield* acquireSupervisor(environmentId);
    return yield* SubscriptionRef.get(supervisor.state);
  });
  const stateChanges = (environmentId: EnvironmentId) =>
    followStream(
      environmentId,
      Stream.unwrap(
        EnvironmentSupervisor.EnvironmentSupervisor.pipe(
          Effect.map((supervisor) => SubscriptionRef.changes(supervisor.state)),
        ),
      ),
    );

  const preparedConnection = (environmentId: EnvironmentId) => {
    const scope = SubscriptionRef.getUnsafe(serviceScopes).get(environmentId);
    return scope === undefined
      ? Option.none<PreparedConnection>()
      : SubscriptionRef.getUnsafe(scope.supervisor.prepared);
  };
  const awaitPreparedConnection = (environmentId: EnvironmentId) =>
    followStream(
      environmentId,
      Stream.unwrap(
        EnvironmentSupervisor.EnvironmentSupervisor.pipe(
          Effect.map((supervisor) => SubscriptionRef.changes(supervisor.prepared)),
        ),
      ),
    ).pipe(
      Stream.filter(Option.isSome),
      Stream.map((prepared) => prepared.value),
      Stream.runHead,
      // `followStream` only ends with the registry, so a missing head means nothing will come.
      Effect.flatMap(Option.match({ onNone: () => Effect.never, onSome: Effect.succeed })),
      Effect.withSpan("EnvironmentRegistry.awaitPreparedConnection"),
    );

  yield* Effect.addFinalizer(closeAllScopes);
  yield* trackClientTransportEffect(
    resources,
    {
      ownerIdentity: "registry",
      kind: "fiber",
      identity: "connectivity-consumer",
      lifetime: "retained-owner",
    },
    trackClientTransportEffect(
      resources,
      {
        ownerIdentity: "registry",
        kind: "subscription",
        identity: "connectivity",
        lifetime: "retained-owner",
      },
      connectivity.changes.pipe(
        Stream.runForEach((status) => SubscriptionRef.set(networkStatus, status)),
      ),
      { interruptionReason: "owner-disposal" },
    ),
    { interruptionReason: "owner-disposal" },
  ).pipe(Effect.forkScoped);

  return EnvironmentRegistry.of({
    entries,
    networkStatus,
    installHost,
    replaceHost,
    removeHost,
    setEnabled,
    retryNow,
    state,
    stateChanges,
    run,
    runStream,
    followStream,
    preparedConnection,
    awaitPreparedConnection,
  });
});

export const layer = Layer.effect(EnvironmentRegistry, make);
